/**
 * Украшения профиля: картинки рамок и эффектов (web/public/decorations, их выгружает
 * docs/decorations/export_web.py) и стиль ника. Номера шрифтов и эффектов — как в
 * backend/internal/decor/look.go.
 */
import type { CSSProperties } from 'react'
import type { AvatarDecoration, NameStyle } from '@/types/api'
import { authed } from './auth'

/** Рамка — 160 % аватара со сдвигом −30 %: так помещаются корона и жетон (docs/decorations/README.md). */
export const FRAME_SCALE = 1.6

/** Рамка по имени из каталога — в том виде, в каком её ждёт Avatar; null — без рамки. */
export const frameOf = (id: string | null): AvatarDecoration | null => (id ? { asset: id, sku_id: '0' } : null)

export function frameSources(d: AvatarDecoration, size: number) {
  const base = `/decorations/frame-${d.asset}`
  return { src: `${base}-256.webp`, srcSet: `${base}-256.webp 256w, ${base}.webp 512w`, sizes: `${Math.round(size * FRAME_SCALE)}px` }
}

/** Табличка: одна картинка, строка списка её растягивает по ширине. */
export const plateUrl = (asset: string) => `/decorations/plate-${asset}.webp`

export function effectSources(id: string) {
  const base = `/decorations/effect-${id}`
  return { src: `${base}.webp`, srcSet: `${base}.webp 480w, ${base}-768.webp 768w` }
}

interface NameFont {
  id: number
  label: string
  family: string
  /** Во сколько раз мельче обычного: пиксельный и моноширинный шрифты заметно шире. */
  scale: number
  /** Файлы шрифта грузятся, только когда такой ник появился на экране. */
  load?: () => Promise<unknown>
}

export const NAME_FONTS: NameFont[] = [
  { id: 0, label: 'Обычный', family: "'Golos Text', system-ui, sans-serif", scale: 1 },
  { id: 1, label: 'Unbounded', family: "'Unbounded', 'Golos Text', sans-serif", scale: 0.92, load: () => import('@fontsource/unbounded/700.css') },
  { id: 2, label: 'Russo One', family: "'Russo One', 'Golos Text', sans-serif", scale: 1, load: () => import('@fontsource/russo-one/400.css') },
  { id: 3, label: 'Пиксели', family: "'Press Start 2P', monospace", scale: 0.62, load: () => import('@fontsource/press-start-2p/400.css') },
  { id: 4, label: 'Pacifico', family: "'Pacifico', cursive", scale: 1.05, load: () => import('@fontsource/pacifico/400.css') },
  { id: 5, label: 'Comfortaa', family: "'Comfortaa', 'Golos Text', sans-serif", scale: 1, load: () => import('@fontsource/comfortaa/700.css') },
  { id: 6, label: 'Rubik Mono One', family: "'Rubik Mono One', monospace", scale: 0.8, load: () => import('@fontsource/rubik-mono-one/400.css') },
]

export const EFFECT_SOLID = 0
export const EFFECT_GRADIENT = 1
export const EFFECT_GLOW = 2
export const EFFECT_NEON = 3

export const NAME_EFFECTS = [
  { id: EFFECT_SOLID, label: 'Цвет' },
  { id: EFFECT_GRADIENT, label: 'Градиент' },
  { id: EFFECT_GLOW, label: 'Свечение' },
  { id: EFFECT_NEON, label: 'Неон' },
]

/** Неон без своего цвета — голубой, как у награды «Диджея». */
const NEON = 0x7fe7ff

const requested = new Set<number>()

export function loadNameFont(id: number) {
  const font = NAME_FONTS[id]
  if (!font?.load || requested.has(id)) return
  requested.add(id)
  font.load().catch(() => requested.delete(id))
}

export const hexColor = (c: number) => `#${c.toString(16).padStart(6, '0')}`
export const colorNumber = (hex: string) => Number.parseInt(hex.replace('#', ''), 16)

/** CSS ника по стилю. Цвет по умолчанию — унаследованный: цвет роли или обычный текст. */
export function nameCss(style: NameStyle): CSSProperties {
  const css: CSSProperties = {}
  const font = NAME_FONTS[style.font_id]
  if (font && font.id !== 0) {
    css.fontFamily = font.family
    if (font.scale !== 1) css.fontSize = `${font.scale}em`
  }
  const [first, second] = style.colors
  switch (style.effect_id) {
    case EFFECT_GRADIENT:
      if (first !== undefined && second !== undefined) {
        css.backgroundImage = `linear-gradient(90deg, ${hexColor(first)}, ${hexColor(second)})`
        css.WebkitBackgroundClip = 'text'
        css.backgroundClip = 'text'
        css.color = 'transparent'
      }
      break
    case EFFECT_GLOW: {
      const c = first !== undefined ? hexColor(first) : 'currentColor'
      if (first !== undefined) css.color = c
      css.textShadow = `0 0 6px color-mix(in srgb, ${c} 75%, transparent), 0 0 14px color-mix(in srgb, ${c} 40%, transparent)`
      break
    }
    case EFFECT_NEON: {
      const c = hexColor(first ?? NEON)
      css.color = c
      css.textShadow = `0 0 4px ${c}, 0 0 12px color-mix(in srgb, ${c} 80%, transparent), 0 0 22px color-mix(in srgb, ${c} 55%, transparent)`
      break
    }
    default:
      if (first !== undefined) css.color = hexColor(first)
  }
  return css
}

/** Что надето: рамка, эффект профиля, табличка, стиль ника, цвета профиля и тег сервера. */
export interface Look {
  frame: string | null
  effect: string | null
  plate: string | null
  style: NameStyle | null
  theme: number[] | null
  tag: { tag: string; color: number } | null
}

/** Предметы поверх надетого: «frame:camo», «effect:snow», «plate:space» или «name:neon» — для примерки. */
export function wearing(base: Look, items: string[]): Look {
  const look = { ...base }
  for (const item of items) {
    const [kind, id = ''] = item.split(':')
    if (kind === 'frame') look.frame = id
    else if (kind === 'effect') look.effect = id
    else if (kind === 'plate') look.plate = id
    else if (kind === 'name' && id === 'neon') look.style = { font_id: base.style?.font_id ?? 0, effect_id: EFFECT_NEON, colors: [] }
  }
  return look
}

/** Состояние предмета: выдан навсегда, по «Плюсу», идёт его сезон или закрыт. */
export type DecorState = 'owned' | 'plus' | 'season' | 'locked'

export interface DecorEntry {
  id: string
  name: string
  state: DecorState
  plus: boolean
  /** За какое достижение и сколько уже набрано (progress и goal — в часах, днях…). */
  achievement: { id: string; name: string; progress?: number; goal?: number; unit?: string } | null
  season: { name: string; dates: string; active: boolean; until: string } | null
}

/** GET /users/@me/decorations — экран «Украшения». */
export interface Decorations {
  frames: DecorEntry[]
  effects: DecorEntry[]
  /** Таблички — фон строки в списке участников. */
  nameplates: DecorEntry[]
  name_style: { state: DecorState; neon: DecorState }
  /** Цвета профиля: с «Плюсом». */
  theme: DecorState
  /** Серверы с тегом, чей тег можно носить. */
  server_tags: { guild_id: string; guild_name: string; guild_icon: string | null; tag: string; color: number }[]
  plus: boolean
  current: {
    avatar_decoration: string | null
    profile_effect: string | null
    nameplate: string | null
    display_name_styles: NameStyle | null
    theme_colors: number[] | null
    profile_showcase: string[]
    server_tag: string | null
  }
}

export const decorApi = {
  inventory: () => authed<Decorations>('/users/@me/decorations'),
  /** Персоналу: выдать навсегда и забрать; item — «frame:klich», «effect:snow», «name:neon». */
  grant: (userId: string, item: string) => authed<{ item: string; added: boolean }>(`/moderation/decorations/users/${userId}/${item}`, { method: 'PUT' }),
  revoke: (userId: string, item: string) => authed<void>(`/moderation/decorations/users/${userId}/${item}`, { method: 'DELETE' }),
}

const dayMonth = (iso: string) => new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })

/** Подпись под плиткой: откуда предмет или почему закрыт. */
export function entryLine(e: DecorEntry): string {
  switch (e.state) {
    case 'owned':
      return e.achievement ? `За «${e.achievement.name}»` : e.season ? `${e.season.name}, получено` : 'Выдано'
    case 'plus':
      return 'Кличат Плюс'
    case 'season':
      return e.season ? `${e.season.name}, до ${dayMonth(e.season.until)}` : 'Сезон'
  }
  if (e.achievement) {
    const { name, progress, goal } = e.achievement
    // «Основатель · 18/25»; у разовых («Первое слово») счётчик ничего не говорит
    return goal && goal > 1 && progress !== undefined ? `«${name}» · ${progress}/${goal}` : `За «${name}»`
  }
  if (e.plus) return 'С Кличат Плюсом'
  return e.season ? `${e.season.name}, ${e.season.dates}` : 'Недоступно'
}

/** Вид украшения в магазине — плитками. */
export type DecorKind = 'frame' | 'effect' | 'plate'

/** «Рамка «Клич»», «Эффект «Снег»», «Табличка «Космос»». */
export const decorLabel = (kind: DecorKind, name: string) => `${kind === 'frame' ? 'Рамка' : kind === 'plate' ? 'Табличка' : 'Эффект'} «${name}»`

/** Почему не сохранить: полное объяснение для строки под кнопкой. */
export function lockReason(kind: DecorKind, e: DecorEntry): string {
  const what = decorLabel(kind, e.name)
  if (e.achievement) {
    const { name, progress, goal, unit } = e.achievement
    const left = goal && goal > 1 && progress !== undefined ? `: ${progress} из ${goal} ${unit ?? ''}`.trimEnd() : ''
    return `${what} — за достижение «${name}»${left}`
  }
  if (e.plus) return `${what} — с Кличат Плюсом`
  if (e.season) return `${what} — в сезон «${e.season.name}», ${e.season.dates}`
  return `${what} пока недоступ${kind === 'effect' ? 'ен' : 'на'}`
}

/** CSS-переменные цветов профиля для карточки: рамка градиентом и фон с оттенком. */
export function themeVars(colors: number[] | null | undefined): CSSProperties | undefined {
  if (!colors || colors.length !== 2) return undefined
  return { '--theme-a': hexColor(colors[0]!), '--theme-b': hexColor(colors[1]!) } as CSSProperties
}
